/**
 * 73.8 (lane H's finding, the Story's stuck relay planter): the HQ's Hesco square (FOOTHOLD_HITBOXES.hq, 67.62 — a team's
 * ecotourism centre where the buildings are sized by function) has one way in and out: the gap in its rear wall, behind the
 * chicane wall. It has no module layout, so the room graph (core/roomGraph.ts) does not route it, and a bot walks straight:
 * one that went in after a seat or a site and was then sent somewhere ahead of the square walked into its own wall for the
 * rest of the match (s71-5: unit 1 pressed against the generator in the square's rear corner from 90 s to the end, a relay
 * 812 people off). Here the square's ways are a fixed handful of points in its frame — the corridors inside round the
 * containers and the generator, the gap, the slot behind it, the chicane's two ends, the four outer corners — and a walker
 * near a standing HQ whose straight way meets one of its parts is walked point by point along the shortest way through them
 * (`footholdStep`, which the bots' walk asks after the room graph: src/ai/actionBot.ts `go`). The points are checked against
 * the hitbox's parts by tests/footholdWays.test.ts, so a change to the hitbox that closes a corridor fails there. Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { FOOTHOLD_HITBOXES } from '../constants/hitboxes';
import { ACTION_UNIT_RADIUS } from '../constants';
import { structureHitbox } from './buildingScale';
import { structureFrame } from './hits';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot, vecLength } from '../math';
import { dacos, dcos, dsin } from './dmath';

/**
 * The ways, in the square's frame (people; x across, z front to back — its front wall at +z, the gap and the chicane at −z):
 * the free corridors' middles inside, the gap's inner mouth, the slot between the rear wall and the chicane, its two ends
 * past the chicane, and the outer corners a body clears.
 */
export const FOOTHOLD_WAYS: ReadonlyArray<{ x: number; z: number }> = [
  { x: 0, z: -2.45 },      // 0 the gap's inner mouth (under the containers)
  { x: 0, z: -4.445 },     // 1 the slot behind the gap (between the rear wall and the chicane)
  { x: 2.2, z: -4.445 },   // 2 the slot's east end, past the chicane
  { x: -2.2, z: -4.445 },  // 3 its west end
  { x: 2.72, z: -2.45 },   // 4 inside: the east corridor's foot
  { x: 2.72, z: 2.77 },    // 5 the east corridor's head (the north strip)
  { x: -2.72, z: 2.77 },   // 6 the north strip's west end
  { x: -2.72, z: 0.8 },    // 7 the west pocket (between the containers and the west wall, above the generator)
  { x: -1.9, z: -2.85 },   // 8 the south strip, by the generator's foot
  { x: -2.75, z: -2.88 },  // 9 the south-west corner (under the generator)
  { x: 4.7, z: -4.7 },     // 10 outside: the south-east corner
  { x: -4.7, z: -4.7 },    // 11 the south-west corner
  { x: 4.7, z: 4.7 },      // 12 the north-east corner
  { x: -4.7, z: 4.7 },     // 13 the north-west corner
];
/** How near (people from its centre) a walker or its goal has to be for the square to be asked: past the outer corners. */
export const FOOTHOLD_REACH_PEOPLE = 7.5;
/** How far along the walk a far goal is cut in the square's frame (people): two reaches, as the room graph cuts one. */
const CUT_PEOPLE = 2 * FOOTHOLD_REACH_PEOPLE;
/** The parts' clearance for a leg between two ways (people past the body's half-width): a way stands clear of it. */
export const FOOTHOLD_WAY_MARGIN = 0.05;
/** A body's half-width (people), read at use (the constants barrel is mid-load when this module first runs). */
const bodyHalf = (): number => ACTION_UNIT_RADIUS / PERSON_BODY_HEIGHT;

/** The hitbox's parts at walking height as rectangles in the square's frame (people): x0, x1, z0, z1. */
function partRects(): ReadonlyArray<readonly [number, number, number, number]> {
  const out: Array<[number, number, number, number]> = [];
  for (const p of FOOTHOLD_HITBOXES.hq.parts) {
    if (p.shape !== 'box' || p.centre[1] - p.half[1] > 1) continue;   // (nothing overhead is walked under)
    out.push([p.centre[0] - p.half[0], p.centre[0] + p.half[0], p.centre[2] - p.half[2], p.centre[2] + p.half[2]]);
  }
  return out;
}
let rectsMemo: ReadonlyArray<readonly [number, number, number, number]> | null = null;
const rects = () => (rectsMemo ??= partRects());

/** Whether the segment (x0, z0)–(x1, z1) passes through a part grown by `grow` (people): the slab test per rectangle. */
export function footholdSegmentBlocked(x0: number, z0: number, x1: number, z1: number, grow: number): boolean {
  const dx = x1 - x0, dz = z1 - z0;
  for (const r of rects()) {
    let t0 = 0, t1 = 1, hit = true;
    for (const [d, p, lo, hi] of [[dx, x0, r[0] - grow, r[1] + grow], [dz, z0, r[2] - grow, r[3] + grow]] as const) {
      if (Math.abs(d) < 1e-12) { if (p <= lo || p >= hi) { hit = false; break; } continue; }
      let a = (lo - p) / d, b = (hi - p) / d;
      if (a > b) { const s = a; a = b; b = s; }
      t0 = Math.max(t0, a); t1 = Math.min(t1, b);
      if (t0 >= t1) { hit = false; break; }
    }
    if (hit) return true;
  }
  return false;
}

/** The legs between ways no part blocks (the ways' own clearance), once. */
let legsMemo: boolean[][] | null = null;
function legs(): boolean[][] {
  if (legsMemo) return legsMemo;
  const n = FOOTHOLD_WAYS.length, grow = bodyHalf() + FOOTHOLD_WAY_MARGIN;
  legsMemo = Array.from({ length: n }, (_, i) => Array.from({ length: n }, (_, j) =>
    i !== j && !footholdSegmentBlocked(FOOTHOLD_WAYS[i].x, FOOTHOLD_WAYS[i].z, FOOTHOLD_WAYS[j].x, FOOTHOLD_WAYS[j].z, grow)));
  return legsMemo;
}

/**
 * The next point on the shortest way from (wx, wz) to (gx, gz) in the square's frame (people), or null when the straight
 * way is open or no way joins them. The walker's and the goal's legs are tested a little inside the body's half-width (a
 * body pressed to a wall stands just inside its grown face); a goal no way reaches (in a part) is left to the walk.
 */
export function footholdLeg(wx: number, wz: number, gx: number, gz: number): { x: number; z: number } | null {
  const grow = bodyHalf() - FOOTHOLD_WAY_MARGIN;
  if (!footholdSegmentBlocked(wx, wz, gx, gz, grow)) return null;
  const n = FOOTHOLD_WAYS.length, L = legs(), G = n;   // (node n is the goal)
  const dist = new Array<number>(n + 1).fill(Infinity), first = new Array<number>(n + 1).fill(-1), done = new Array<boolean>(n + 1).fill(false);
  const len = (ax: number, az: number, bx: number, bz: number) => Math.sqrt((ax - bx) * (ax - bx) + (az - bz) * (az - bz));
  const toGoal = FOOTHOLD_WAYS.map(w => !footholdSegmentBlocked(w.x, w.z, gx, gz, grow));
  for (let i = 0; i < n; i++) {
    const w = FOOTHOLD_WAYS[i];
    if (!footholdSegmentBlocked(wx, wz, w.x, w.z, grow)) { dist[i] = len(wx, wz, w.x, w.z); first[i] = i; }
  }
  for (;;) {
    let u = -1;
    for (let i = 0; i <= n; i++) if (!done[i] && dist[i] < Infinity && (u < 0 || dist[i] < dist[u])) u = i;
    if (u < 0 || u === G) break;
    done[u] = true;
    const a = FOOTHOLD_WAYS[u];
    for (let v = 0; v < n; v++) {
      if (!L[u][v] || done[v]) continue;
      const d = dist[u] + len(a.x, a.z, FOOTHOLD_WAYS[v].x, FOOTHOLD_WAYS[v].z);
      if (d < dist[v]) { dist[v] = d; first[v] = first[u]; }
    }
    if (toGoal[u]) {
      const d = dist[u] + len(a.x, a.z, gx, gz);
      if (d < dist[G]) { dist[G] = d; first[G] = first[u]; }
    }
  }
  return first[G] >= 0 ? FOOTHOLD_WAYS[first[G]] : null;
}

/** A standing HQ's frame on its planet: its base, axes and the person (world units). */
interface FootholdFrame { base: Vec3; up: Vec3; tx: Vec3; tz: Vec3; person: number }
const frameMemo = new WeakMap<StructureState, FootholdFrame | null>();
const listMemo = new WeakMap<readonly StructureState[], Map<number, readonly FootholdFrame[]>>();

/** The standing HQs drawn as the Hesco square on a planet, framed; memoised per structures array and per structure. */
function footholdFrames(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number): readonly FootholdFrame[] {
  let byPlanet = listMemo.get(state.structures);
  if (!byPlanet) { byPlanet = new Map(); listMemo.set(state.structures, byPlanet); }
  const hit = byPlanet.get(planetIndex);
  if (hit) return hit;
  const out: FootholdFrame[] = [];
  const planet = state.planets[planetIndex];
  if (planet) for (const s of state.structures) {
    if (s.planetIndex !== planetIndex || s.health <= 0 || s.type !== 'ecotourism') continue;
    let f = frameMemo.get(s);
    if (f === undefined) {
      if (structureHitbox(s, state.config) !== FOOTHOLD_HITBOXES.hq) f = null;
      else {
        const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
        const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));
        f = { base, up, tx, tz, person: PERSON_BODY_HEIGHT * personSetOf(state).k };
      }
      frameMemo.set(s, f);
    }
    if (f) out.push(f);
  }
  byPlanet.set(planetIndex, out);
  return out;
}

/** A direction's point on the ground in the square's frame (people), at the base's own radius (as roomGraph's localOf). */
function localOf(f: FootholdFrame, planet: { position: Vec3 }, dir: Vec3): { x: number; z: number } {
  const p = vecSub(vecAdd(planet.position, vecScale(dir, vecLength(vecSub(f.base, planet.position)))), f.base);
  return { x: vecDot(p, f.tx) / f.person, z: vecDot(p, f.tz) / f.person };
}

/**
 * The walk's question (src/ai/actionBot.ts `go`, after the room graph's): for a walker at `walker` going to `goal` (directions
 * from the planet's centre), the next point to walk to round or through an HQ's Hesco square whose parts its straight way
 * meets — a direction from the planet's centre — or null (no square near, the way open, or none joins them: the walk as it was).
 */
export function footholdStep(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number, walker: Vec3, goal: Vec3): Vec3 | null {
  const frames = footholdFrames(state, planetIndex);
  if (!frames.length) return null;
  const planet = state.planets[planetIndex], R = planet.radius;
  const wl = 1 / Math.sqrt(walker.x * walker.x + walker.y * walker.y + walker.z * walker.z), gl = 1 / Math.sqrt(goal.x * goal.x + goal.y * goal.y + goal.z * goal.z);
  const nx = walker.x * wl, ny = walker.y * wl, nz = walker.z * wl;
  const cosA = Math.max(-1, Math.min(1, nx * goal.x * gl + ny * goal.y * gl + nz * goal.z * gl)), arc = dacos(cosA);
  const hx = goal.x * gl - nx * cosA, hy = goal.y * gl - ny * cosA, hz = goal.z * gl - nz * cosA, hl = Math.sqrt(hx * hx + hy * hy + hz * hz);
  for (const f of frames) {
    // the far test in scalars: the walker's angle to the square past its reach plus the walk's cut leg, and it is skipped
    const reach = FOOTHOLD_REACH_PEOPLE * f.person, cut = (CUT_PEOPLE * f.person) / R;
    const toSquare = dacos(Math.max(-1, Math.min(1, nx * f.up.x + ny * f.up.y + nz * f.up.z)));
    if (toSquare * R > reach + R * Math.min(arc, cut)) continue;
    const goalF = arc > cut && hl > 1e-9 ? { x: nx * dcos(cut) + (hx / hl) * dsin(cut), y: ny * dcos(cut) + (hy / hl) * dsin(cut), z: nz * dcos(cut) + (hz / hl) * dsin(cut) } : goal;
    const w = localOf(f, planet, walker), g = localOf(f, planet, goalF);
    const r = FOOTHOLD_REACH_PEOPLE;
    if (w.x * w.x + w.z * w.z >= r * r && g.x * g.x + g.z * g.z >= r * r) {
      // neither near: the straight way passing within reach still asks (a walk across the square's middle)
      const dx = g.x - w.x, dz = g.z - w.z, l2 = dx * dx + dz * dz;
      if (l2 < 1e-12) continue;
      const t = Math.max(0, Math.min(1, -(w.x * dx + w.z * dz) / l2)), px = w.x + dx * t, pz = w.z + dz * t;
      if (px * px + pz * pz >= r * r) continue;
    }
    const leg = footholdLeg(w.x, w.z, g.x, g.z);
    if (!leg) continue;
    return vecNormalize(vecSub(vecAdd(f.base, vecAdd(vecScale(f.tx, leg.x * f.person), vecScale(f.tz, leg.z * f.person))), planet.position));
  }
  return null;
}
